/**
 * The reports the assistant can count.
 *
 * Each entry is roughly ten lines of data and yields a working "how many X"
 * skill for both the planner and the deterministic fallback. Adding a report
 * means adding an entry here — no conversation code, no request module, no
 * answer function.
 *
 * Two rules for new entries, both learned the hard way:
 *
 * 1. `supportsPeriod` must reflect what the **endpoint** does, not what would
 *    be convenient. Every entry below was checked against the request module
 *    the app already uses for that report. If the server ignores `startDate`,
 *    saying "last month" would be a wrong answer wearing a precise-looking
 *    label.
 * 2. Synonyms must be built on the report's own domain noun. Generic phrasings
 *    collide, and `knowledgeRegistry.collisions.test` will fail loudly if two
 *    skills ever contest the same phrase — that check is the reason this file
 *    can grow safely.
 */

import {
  accountEditEndpoints,
  adminInvoicesReportEndpoints,
  buyerBookingsReportEndpoints,
  buyerInterchangesReportEndpoints,
  buyerInventoryReportEndpoints,
  buyerInvoicesReportEndpoints,
  observableEndpoints,
  supplierCurrentInventoryReportEndpoints,
  supplierInterchangesReportEndpoints,
  supplierInvoicesReportEndpoints,
} from '../../requests/api-endpoints';
import { AccountType } from '../constants/securspace-constants';
import type { ReportDescriptor } from './reportSkills';

export const REPORT_DESCRIPTORS: ReportDescriptor[] = [
  {
    id: 'supplierInterchanges',
    audiences: ['supplier'],
    searchEndpoint: supplierInterchangesReportEndpoints.search,
    noun: 'interchange',
    synonyms: [
      'how many interchanges',
      'number of interchanges',
      'how many gate moves',
      'how many check ins and check outs',
    ],
    description:
      'The number of gate interchanges — check-ins and check-outs recorded at ' +
      'the supplier\u2019s locations — over a time period. Counts gate activity ' +
      'events, not bookings and not what is currently on site.',
    example: 'how many interchanges did I have last month',
    supportsPeriod: true,
    supportsLocation: true,
  },
  {
    id: 'supplierCurrentInventory',
    audiences: ['supplier'],
    searchEndpoint: supplierCurrentInventoryReportEndpoints.search,
    noun: 'piece of equipment',
    nounPlural: 'pieces of equipment',
    synonyms: [
      'how many trucks are on site',
      'how much is on site',
      'what is on my lot',
      'how many containers are on site',
      'how many containers do i have dwelling',
      'how many pieces of equipment are in yards right now',
      'how many pieces of equipment are in the yards right now',
      'how much equipment is on the yard',
      'how much equipment is on site',
      'current inventory',
    ],
    /*
     * "How much equipment is on the yards at all my locations currently" is the
     * same question as "how many trucks are on site" and shares not one word
     * with it. Enumerating the phrasings is hopeless — "equipment/trucks/
     * containers/trailers/chassis" x "on site/on the yard/on my lot/parked" x
     * "how much/how many/what is" is hundreds of sentences.
     *
     * "inventory" is deliberately **not** a concept word here even though it is
     * a synonym: this account type also has an inventory-corrections skill, and
     * a phrasing like "what inventory corrections are open" that missed that
     * skill's synonyms would then be answered with a yard count — a confidently
     * wrong number rather than a decline.
     */
    requires: [
      ['many', 'much', 'count', 'number', 'total', 'amount'],
      [
        'equipment',
        'truck',
        'trucks',
        'container',
        'containers',
        'trailer',
        'trailers',
        'chassis',
        'yard',
        'yards',
      ],
    ],
    description:
      'How much equipment is physically on site at the supplier\u2019s locations ' +
      'right now — current occupancy. A live snapshot, so it ignores any time ' +
      'period. Not the number of bookings and not total capacity.',
    // A live snapshot: the endpoint reports what is on the lot now, so a date
    // filter would not mean anything here.
    supportsPeriod: false,
    supportsLocation: true,
    example: 'how many trucks are on site right now',
    countContext: 'in Current Inventory',
    link: { label: 'Open Current Inventory', path: '/supplier-inventory' },
  },
  {
    id: 'supplierInvoiceCount',
    audiences: ['supplier'],
    searchEndpoint: supplierInvoicesReportEndpoints.report,
    noun: 'invoice',
    synonyms: ['how many invoices', 'number of invoices', 'how many invoices did i issue'],
    description:
      'The number of invoices raised to the supplier\u2019s buyers over a time ' +
      'period. A count of invoices, not their value — use the revenue or ' +
      'receivables skills for amounts.',
    example: 'how many invoices did I have last month',
    supportsPeriod: true,
    supportsLocation: true,
  },
  {
    id: 'buyerBookingCount',
    audiences: ['buyer'],
    searchEndpoint: buyerBookingsReportEndpoints.search,
    noun: 'booking',
    synonyms: [
      'how many bookings',
      'number of bookings',
      'how many bookings do i have',
      'how many spaces have i booked',
    ],
    description:
      'The number of bookings the buyer has made over a time period.',
    example: 'how many bookings do I have',
    supportsPeriod: true,
    supportsLocation: true,
    link: { label: 'Open My Bookings', path: '/bookings' },
  },
  {
    id: 'buyerInvoiceCount',
    audiences: ['buyer'],
    searchEndpoint: buyerInvoicesReportEndpoints.search,
    noun: 'invoice',
    synonyms: ['how many invoices', 'number of invoices', 'how many invoices do i have'],
    description:
      'The number of invoices billed to the buyer over a time period. A count, ' +
      'not an amount owed.',
    example: 'how many invoices do I have',
    supportsPeriod: true,
    supportsLocation: true,
  },
  {
    id: 'buyerInventoryCount',
    audiences: ['buyer'],
    searchEndpoint: buyerInventoryReportEndpoints.search,
    noun: 'piece of equipment',
    nounPlural: 'pieces of equipment',
    synonyms: [
      'how much equipment do i have parked',
      'how many trucks do i have parked',
      'how much of my equipment is stored',
      'how much equipment do i have on site',
      'how many containers do i have dwelling',
      'how many pieces of equipment are in yards right now',
      'how many pieces of equipment are checked into yards',
    ],
    /* Same combinatorics as the supplier yard count; see the note there. */
    requires: [
      ['many', 'much', 'count', 'number', 'total', 'amount'],
      [
        'equipment',
        'truck',
        'trucks',
        'container',
        'containers',
        'trailer',
        'trailers',
        'chassis',
        'yard',
        'yards',
        'dwelling',
        'checked',
        'check',
        'site',
      ],
    ],
    description:
      'How much of the buyer\u2019s own equipment is currently in supplier yards ' +
      'right now (Current Inventory). A live snapshot that ignores time periods.',
    example: 'how much equipment do I have parked',
    supportsPeriod: false,
    supportsLocation: true,
    countContext: 'in Current Inventory',
    link: { label: 'Open Current Inventory', path: '/buyer-inventory' },
  },
  {
    id: 'buyerInterchangeCount',
    audiences: ['buyer'],
    searchEndpoint: buyerInterchangesReportEndpoints.search,
    noun: 'interchange',
    synonyms: [
      'how many interchanges',
      'number of interchanges',
      'how many gate moves',
    ],
    description:
      'The number of gate interchanges \u2014 check-ins and check-outs of the ' +
      'buyer\u2019s equipment \u2014 over a time period.',
    example: 'how many interchanges did I have last month',
    supportsPeriod: true,
    supportsLocation: true,
  },
  /*
   * The live-count endpoints below are the same ones behind the navigation
   * badges, so the assistant's answer and the number the user can already see
   * on screen cannot disagree. They take no filters: each is a current backlog,
   * not a flow, so there is no period or location to apply.
   */
  {
    id: 'supplierPendingApprovals',
    audiences: ['supplier'],
    searchEndpoint: observableEndpoints.supplier.pendingBookingsCount,
    noun: 'booking waiting for approval',
    nounPlural: 'bookings waiting for approval',
    synonyms: [
      'how many bookings are waiting for approval',
      'how many pending approvals',
      'what needs my approval',
      'how many bookings need approving',
    ],
    description:
      'How many booking requests are waiting on the supplier to approve or ' +
      'decline them right now. A work queue, so it has no time period. Not a ' +
      'count of active bookings.',
    example: 'how many bookings are waiting for approval',
    supportsPeriod: false,
    supportsLocation: false,
    link: { label: 'Open Booking Approvals', path: '/approvals' },
  },
  {
    id: 'supplierPendingInventoryActivity',
    audiences: ['supplier'],
    searchEndpoint: observableEndpoints.supplier.pendingInventoryActivityCount,
    noun: 'pending gate activity',
    nounPlural: 'pending gate activities',
    synonyms: [
      'how many pending gate activities',
      'how much gate activity is pending',
      'how many activities need attention',
    ],
    description:
      'How many gate activities are stuck pending and need the supplier to ' +
      'resolve them right now. A work queue with no time period.',
    example: 'how many pending gate activities do I have',
    supportsPeriod: false,
    supportsLocation: false,
    link: { label: 'Open Pending Inventory Activity', path: '/supplier-pending-inventory-activity' },
  },
  {
    id: 'supplierInventoryCorrections',
    audiences: ['supplier'],
    searchEndpoint: observableEndpoints.supplier.inventoryCorrectionsCount,
    noun: 'inventory correction',
    synonyms: [
      'how many inventory corrections',
      'how many corrections are open',
      'how many corrections do i have',
    ],
    description:
      'How many inventory corrections are outstanding for the supplier right ' +
      'now. A work queue with no time period.',
    example: 'how many inventory corrections do I have',
    supportsPeriod: false,
    supportsLocation: false,
    link: { label: 'Open Inventory Corrections', path: '/inventory-corrections' },
  },
  {
    id: 'buyerInventoryCorrections',
    audiences: ['buyer'],
    searchEndpoint: observableEndpoints.buyer.inventoryCorrectionsCount,
    noun: 'inventory correction',
    synonyms: [
      'how many inventory corrections',
      'how many corrections are open',
      'how many corrections do i have',
    ],
    description:
      'How many inventory corrections are outstanding for the buyer right ' +
      'now. A work queue with no time period.',
    example: 'how many inventory corrections do I have',
    supportsPeriod: false,
    supportsLocation: false,
    link: { label: 'Open Inventory Corrections', path: '/buyer-inventory-corrections' },
  },
  {
    id: 'adminInvoiceCount',
    audiences: ['admin'],
    searchEndpoint: adminInvoicesReportEndpoints.search,
    noun: 'invoice',
    // An admin sees the whole platform, so "you have" would misdescribe whose
    // invoices these are.
    subject: {
      present: { singular: 'There is', plural: 'There are' },
      past: { singular: 'There was', plural: 'There were' },
    },
    synonyms: ['how many invoices', 'number of invoices', 'how many invoices are there'],
    description:
      'The number of invoices raised across the entire platform over a time ' +
      'period, for an administrator. Platform-wide, not one account\u2019s.',
    example: 'how many invoices were there last month',
    supportsPeriod: true,
    supportsLocation: false,
  },
  {
    id: 'adminActiveSupplierCount',
    audiences: ['admin'],
    searchEndpoint: accountEditEndpoints.adminAccountReport.getAccountReport,
    noun: 'active supplier account',
    // Platform-wide, same reasoning as adminInvoiceCount above.
    subject: {
      present: { singular: 'There is', plural: 'There are' },
      past: { singular: 'There was', plural: 'There were' },
    },
    synonyms: [
      'how many active suppliers are there',
      'how many suppliers are there',
      'how many active supplier accounts are there',
      'how many supplier accounts are there',
      'how many active suppliers do we have',
      'how many suppliers do we have',
      'number of active suppliers',
      'number of suppliers',
    ],
    description:
      'The number of active (enabled) supplier accounts on the platform, for an ' +
      'administrator. Counts accounts, not locations or bookings \u2014 a supplier ' +
      'with several locations is still one account.',
    example: 'how many active suppliers are there',
    supportsPeriod: false,
    supportsLocation: false,
    fixedParams: { type: AccountType.SUPPLIER, activated: 'true' },
    link: { label: 'Open Admin Accounts', path: '/admin-accounts' },
  },
  {
    id: 'adminActiveBuyerCount',
    audiences: ['admin'],
    searchEndpoint: accountEditEndpoints.adminAccountReport.getAccountReport,
    noun: 'active buyer account',
    subject: {
      present: { singular: 'There is', plural: 'There are' },
      past: { singular: 'There was', plural: 'There were' },
    },
    synonyms: [
      'how many active buyers are there',
      'how many buyers are there',
      'how many active buyer accounts are there',
      'how many buyer accounts are there',
      'how many active buyers do we have',
      'how many buyers do we have',
      'number of active buyers',
      'number of buyers',
    ],
    description:
      'The number of active (enabled) buyer accounts on the platform, for an ' +
      'administrator. Counts accounts, not bookings \u2014 a buyer with many ' +
      'bookings is still one account.',
    example: 'how many active buyers are there',
    supportsPeriod: false,
    supportsLocation: false,
    fixedParams: { type: AccountType.BUYER, activated: 'true' },
    link: { label: 'Open Admin Accounts', path: '/admin-accounts' },
  },
];
